import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { readStampedChipForUser, scrubStampedHistoryState } from '../../lib/stampedSurfaceState';
import styles from './DraftLandedChip.module.css';

/**
 * B-3 — the landed ✓ chip (§1g). After approve, the draft takeover navigates to
 * the origin surface (Today / Season plan / Week N) with a transient confirmation
 * in `location.state` (e.g. "Week 2 is in — 4 runs, 13 miles."). This one quiet
 * `--ok-soft` pill surfaces it, auto-dismisses (~5s), and announces it once via a
 * `polite` live region. Self-gating: renders nothing without the state, so it's
 * inert on every ordinary navigation. Mounted once globally (App.tsx), a sibling
 * of the nav — no per-route wiring.
 *
 * B46 (founder, 2026-08-30) — that global mount is also what made this pill the
 * one surface a leak could show up on EVERYWHERE, and it did: a brand-new
 * account, freshly signed up on a device whose previous session was a different
 * account, saw the prior user's "Week 9 is in." through onboarding and in the
 * header. Two independent faults, both fixed here:
 *
 *  ① NO OWNERSHIP. The chip was rendered by whoever was signed in when the
 *    router happened to carry it. `location.state` lives in
 *    `window.history.state.usr`, which the browser/WKWebView persists with the
 *    session history entry and restores across reloads and warm relaunches —
 *    so it outlives the account that minted it. Fixed by stamping the chip with
 *    its author at mint (`lib/stampedSurfaceState`) and rendering only for that
 *    author; see that module for why UNSTAMPED drops here while B26's wizard
 *    blob keeps unclaimed state.
 *
 *  ② A STUCK PILL. The dismiss timer was created in — and torn down by — the
 *    effect keyed on the INCOMING chip. Navigating anywhere within the 5s
 *    window flipped `incoming` to null, whose cleanup cancelled the timer
 *    before the early return could start a new one: `text` was never cleared
 *    again, so the pill stayed painted for the rest of the SPA session. That's
 *    how a five-second toast survived a sign-out and rode into the next
 *    account's onboarding. The timer now hangs off the SHOWN text, so it fires
 *    regardless of where the user navigates.
 *
 * The identity-change effect below is this lane's equivalent of B26's
 * purge-on-sign-in. It lives on the surface rather than in AuthContext because
 * this component is mounted globally and unconditionally, so it observes every
 * auth settle the provider does — and the state being purged is the router's,
 * which is this surface's own business.
 */
const DISMISS_MS = 5000;

export function DraftLandedChip() {
  const location = useLocation();
  const { user } = useAuthContext();
  const viewerId = user?.id ?? null;
  // Ownership-aware read — a chip stamped by anyone else is simply absent.
  const incoming = readStampedChipForUser(location.state, viewerId);
  const [text, setText] = useState<string | null>(null);
  const shownRef = useRef<string | null>(null);
  const viewerRef = useRef<string | null>(null);
  const identitySettled = useRef(false);

  // B46 ① — the identity fence. Any change of signed-in user (A → null on
  // sign-out, null → B on the new sign-up, A → B on a direct switch) drops
  // whatever this surface is painting and scrubs the router state out of the
  // history entry, so a later reload can't resurrect it under the new account.
  useEffect(() => {
    if (identitySettled.current && viewerRef.current === viewerId) return;
    const changed = identitySettled.current;
    identitySettled.current = true;
    viewerRef.current = viewerId;
    if (!changed) return; // first settle — nothing painted yet to clear.
    shownRef.current = null;
    setText(null);
    scrubStampedHistoryState();
  }, [viewerId]);

  // Adopt an incoming chip once. Runs after the fence above in the same commit,
  // so a chip that arrives together with a fresh sign-in still shows.
  useEffect(() => {
    if (!incoming || shownRef.current === incoming) return;
    shownRef.current = incoming;
    setText(incoming);
    // Strip the state so a refresh / back doesn't re-show the chip.
    scrubStampedHistoryState();
  }, [incoming]);

  // B46 ② — the dismiss timer belongs to the SHOWN text, not to the incoming
  // router state. Keyed this way it cannot be cancelled by an unrelated
  // navigation, which is what left the pill painted forever.
  useEffect(() => {
    if (!text) return;
    const t = window.setTimeout(() => setText(null), DISMISS_MS);
    return () => window.clearTimeout(t);
  }, [text]);

  if (!text) return null;

  return (
    <div className={styles.wrap} role="status" aria-live="polite">
      <span className={styles.pill}>
        <span className={styles.check} aria-hidden>✓</span>
        {text}
      </span>
    </div>
  );
}

export default DraftLandedChip;
